// PPP Admin Console - Superadmin Fleet Management Logic

const DEFAULT_WORKER_URL = 'https://ppp-test-worker.theasmat.workers.dev';
let currentWorkerUrl = localStorage.getItem('ppp_admin_worker_url') || DEFAULT_WORKER_URL;
let currentAdminSecret = localStorage.getItem('ppp_admin_secret') || 'test-user-passkey-123';

// DOM Elements: Navigation
const tabBtns = document.querySelectorAll('.tab-btn');
const tabContents = document.querySelectorAll('.tab-content');

tabBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    tabBtns.forEach((b) => b.classList.remove('active'));
    tabContents.forEach((c) => c.classList.remove('active'));

    btn.classList.add('active');
    const tabName = btn.getAttribute('data-tab');
    const target = document.getElementById(`tab-${tabName}`);
    if (target) target.classList.add('active');
  });
});

// Toast Helper
const toastEl = document.getElementById('toast') as HTMLDivElement;
function showToast(message: string, isError = false) {
  if (!toastEl) return;
  toastEl.textContent = message;
  toastEl.style.borderColor = isError ? 'var(--color-danger)' : 'var(--border-subtle)';
  toastEl.classList.remove('hidden');
  setTimeout(() => {
    toastEl.classList.add('hidden');
  }, 3500);
}

// Global Copy Helper
(window as any).copyToClipboard = (text: string, label = 'Item') => {
  navigator.clipboard.writeText(text).then(() => {
    showToast(`Copied ${label} to clipboard!`);
  }).catch(() => {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    showToast(`Copied ${label} to clipboard!`);
  });
};

// API Request Helper
async function adminApiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T | null> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Access-Token': currentAdminSecret,
    'Authorization': `Bearer ${currentAdminSecret}`,
    ...(options.headers as any)
  };

  const url = endpoint.startsWith('http') ? endpoint : `${currentWorkerUrl}${endpoint}`;
  try {
    const res = await fetch(url, { ...options, headers });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `HTTP ${res.status}`);
    }
    return await res.json();
  } catch (err: any) {
    console.error('Admin API error:', err);
    return null;
  }
}

// -------------------------------------------------------------
// TAB 1: 1-Click BYOC Provisioning Engine
// -------------------------------------------------------------
const provisionForm = document.getElementById('provisionForm') as HTMLFormElement;
const provMerchantName = document.getElementById('provMerchantName') as HTMLInputElement;
const provMerchantDomain = document.getElementById('provMerchantDomain') as HTMLInputElement;
const provMerchantId = document.getElementById('provMerchantId') as HTMLInputElement;
const provDailyLimit = document.getElementById('provDailyLimit') as HTMLInputElement;
const generateProvisionBtn = document.getElementById('generateProvisionBtn') as HTMLButtonElement;
const sealedLinkDisplayBox = document.getElementById('sealedLinkDisplayBox') as HTMLDivElement;
const sealedLinkInput = document.getElementById('sealedLinkInput') as HTMLInputElement;
const copySealedLinkBtn = document.getElementById('copySealedLinkBtn') as HTMLButtonElement;
const cfDeployAnchor = document.getElementById('cfDeployAnchor') as HTMLAnchorElement;
const recordMerchantBtn = document.getElementById('recordMerchantBtn') as HTMLButtonElement;
const nodesRegistryContainer = document.getElementById('nodesRegistryContainer') as HTMLDivElement;
const refreshNodesBtn = document.getElementById('refreshNodesBtn') as HTMLButtonElement;

interface ProvisionedNode {
  merchantId: string;
  name: string;
  domain: string;
  dailyLimit: number;
  workerUrl?: string;
  sealedToken: string;
  createdAt: number;
  status: 'PROVISIONED' | 'ACTIVE';
}

function getStoredNodes(): ProvisionedNode[] {
  try {
    const raw = localStorage.getItem('ppp_admin_nodes');
    return raw ? JSON.parse(raw) : [
      {
        merchantId: 'primary',
        name: 'Primary Store (Main Node)',
        domain: 'payment-dashboard-9jc.pages.dev',
        dailyLimit: 200000,
        workerUrl: currentWorkerUrl,
        sealedToken: 'primary-token',
        createdAt: Date.now() - 86400000,
        status: 'ACTIVE'
      }
    ];
  } catch {
    return [];
  }
}

function saveStoredNodes(nodes: ProvisionedNode[]) {
  localStorage.setItem('ppp_admin_nodes', JSON.stringify(nodes));
}

let lastGeneratedNode: ProvisionedNode | null = null;

if (provMerchantName && provMerchantId) {
  provMerchantName.addEventListener('input', () => {
    const raw = provMerchantName.value.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');
    provMerchantId.value = raw ? `merch_${raw}` : '';
  });
}

if (provisionForm) {
  provisionForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const name = provMerchantName.value.trim();
    const domain = provMerchantDomain.value.trim().toLowerCase().replace(/^https?:\/\//, '');
    const merchantId = provMerchantId.value.trim() || `merch_${name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
    const dailyLimit = Number(provDailyLimit.value) || 100000;

    if (!name || !domain) {
      showToast('Merchant Name and Domain are required', true);
      return;
    }

    const payload = {
      merchantId,
      merchantName: name,
      domain,
      dailyLimit,
      issuedAt: Date.now(),
      salt: crypto.randomUUID().slice(0, 12),
      version: '2.0.0-byoc'
    };

    const token = btoa(JSON.stringify(payload));
    const setupUrl = `https://payment-dashboard-9jc.pages.dev/?m=${encodeURIComponent(merchantId)}&token=${encodeURIComponent(token)}`;
    const cfOneClickDeployUrl = `https://deploy.workers.cloudflare.com/?url=https://github.com/theasmat/phonepay_proxy`;

    if (sealedLinkInput) sealedLinkInput.value = setupUrl;
    if (cfDeployAnchor) cfDeployAnchor.href = cfOneClickDeployUrl;
    if (sealedLinkDisplayBox) sealedLinkDisplayBox.classList.remove('hidden');

    lastGeneratedNode = {
      merchantId,
      name,
      domain,
      dailyLimit,
      sealedToken: token,
      createdAt: Date.now(),
      status: 'PROVISIONED'
    };

    showToast(`Sealed deployment link generated for ${name}!`);
  });
}

if (copySealedLinkBtn && sealedLinkInput) {
  copySealedLinkBtn.addEventListener('click', () => {
    if (sealedLinkInput.value) {
      (window as any).copyToClipboard(sealedLinkInput.value, 'Sealed Setup Link');
    }
  });
}

if (recordMerchantBtn) {
  recordMerchantBtn.addEventListener('click', () => {
    if (!lastGeneratedNode) return;
    const nodes = getStoredNodes();
    if (!nodes.some(n => n.merchantId === lastGeneratedNode?.merchantId)) {
      nodes.unshift(lastGeneratedNode);
      saveStoredNodes(nodes);
      renderNodesRegistry();
      renderFleetCards();
      showToast(`Node '${lastGeneratedNode.name}' saved to fleet registry!`);
    } else {
      showToast(`Node '${lastGeneratedNode.name}' is already in the registry.`);
    }
  });
}

function renderNodesRegistry() {
  if (!nodesRegistryContainer) return;
  const nodes = getStoredNodes();

  if (nodes.length === 0) {
    nodesRegistryContainer.innerHTML = '<div class="table-placeholder">No provisioned merchant nodes yet. Generate one above.</div>';
    return;
  }

  nodesRegistryContainer.innerHTML = nodes.map((n) => {
    const timeText = new Date(n.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    return `
      <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid var(--border-subtle); border-radius: 10px; padding: 12px; display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap;">
        <div style="display: flex; flex-direction: column; gap: 2px;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-weight: 700; color: #818cf8; font-size: 13px;">${n.name}</span>
            <span class="badge badge-settled" style="font-size: 9px; padding: 2px 6px;">${n.status}</span>
          </div>
          <div style="font-family: var(--font-mono); font-size: 11px; color: var(--text-dim);">
            ${n.domain} • Daily Cap: ₹${n.dailyLimit.toLocaleString('en-IN')} • ID: ${n.merchantId}
          </div>
          <div style="font-size: 10px; color: var(--text-dim);">Created: ${timeText}</div>
        </div>
        <div style="display: flex; gap: 6px;">
          <button type="button" class="btn btn-secondary btn-sm" onclick="copyToClipboard('${n.sealedToken}', 'Token')">Copy Token</button>
          <button type="button" class="btn btn-secondary btn-sm" style="color: #f87171;" onclick="deleteNode('${n.merchantId}')">Remove</button>
        </div>
      </div>
    `;
  }).join('');
}

(window as any).deleteNode = (merchantId: string) => {
  if (!confirm(`Remove merchant node '${merchantId}' from registry?`)) return;
  let nodes = getStoredNodes();
  nodes = nodes.filter(n => n.merchantId !== merchantId);
  saveStoredNodes(nodes);
  renderNodesRegistry();
  renderFleetCards();
  showToast('Node removed from registry.');
};

if (refreshNodesBtn) {
  refreshNodesBtn.addEventListener('click', () => {
    renderNodesRegistry();
    showToast('Registry refreshed.');
  });
}

// -------------------------------------------------------------
// TAB 2: Fleet Health Monitor
// -------------------------------------------------------------
const fleetCardsContainer = document.getElementById('fleetCardsContainer') as HTMLDivElement;

async function renderFleetCards() {
  if (!fleetCardsContainer) return;
  const nodes = getStoredNodes();

  fleetCardsContainer.innerHTML = nodes.map((n) => {
    const testUrl = n.workerUrl || currentWorkerUrl;
    return `
      <div class="metric-card" id="card-${n.merchantId}" style="padding: 16px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <h4 style="font-size: 13px; font-weight: 700; margin: 0; color: #f8fafc;">${n.name}</h4>
          <span id="badge-${n.merchantId}" class="badge badge-settled" style="font-size: 9px;">CHECKING</span>
        </div>
        <div style="font-family: var(--font-mono); font-size: 11px; color: #818cf8; margin-bottom: 8px;">
          ${n.domain}
        </div>
        <div style="background: rgba(2, 6, 23, 0.5); border-radius: 6px; padding: 10px; margin-bottom: 10px; font-size: 11px; display: flex; flex-direction: column; gap: 4px;">
          <div style="display: flex; justify-content: space-between;">
            <span style="color: var(--text-dim);">Worker Endpoint:</span>
            <span style="font-family: var(--font-mono); color: var(--text-main); font-size: 10px;">${testUrl.replace('https://', '').slice(0, 24)}...</span>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span style="color: var(--text-dim);">Daily Cap:</span>
            <span style="font-weight: 700; color: #34d399;">₹${n.dailyLimit.toLocaleString('en-IN')}</span>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span style="color: var(--text-dim);">Latency:</span>
            <span id="latency-${n.merchantId}" style="font-family: var(--font-mono); color: var(--text-dim);">--</span>
          </div>
        </div>
        <div style="display: flex; gap: 6px;">
          <button type="button" class="btn btn-secondary btn-sm" style="flex: 1;" onclick="pingNode('${n.merchantId}', '${testUrl}')">
            ⚡ Ping Health
          </button>
        </div>
      </div>
    `;
  }).join('');

  // Auto-ping first node
  if (nodes.length > 0) {
    (window as any).pingNode(nodes[0].merchantId, nodes[0].workerUrl || currentWorkerUrl);
  }
}

(window as any).pingNode = async (merchantId: string, url: string) => {
  const badgeEl = document.getElementById(`badge-${merchantId}`);
  const latencyEl = document.getElementById(`latency-${merchantId}`);
  if (badgeEl) badgeEl.textContent = 'PINGING...';

  const start = performance.now();
  try {
    const resp = await fetch(`${url}/api/health`, { method: 'GET' });
    const latency = Math.round(performance.now() - start);

    if (resp.ok) {
      if (badgeEl) {
        badgeEl.className = 'badge badge-settled';
        badgeEl.textContent = 'ONLINE (200 OK)';
      }
      if (latencyEl) {
        latencyEl.textContent = `${latency}ms`;
        latencyEl.style.color = '#34d399';
      }
      showToast(`Node ${merchantId} responded in ${latency}ms!`);
    } else {
      throw new Error(`HTTP ${resp.status}`);
    }
  } catch (e: any) {
    if (badgeEl) {
      badgeEl.className = 'badge badge-unsettled';
      badgeEl.textContent = 'UNREACHABLE';
    }
    if (latencyEl) {
      latencyEl.textContent = 'ERR';
      latencyEl.style.color = '#f87171';
    }
    showToast(`Node ${merchantId} ping failed: ${e.message}`, true);
  }
};

// -------------------------------------------------------------
// TAB 3: Master System Config
// -------------------------------------------------------------
const sysWorkerUrl = document.getElementById('sysWorkerUrl') as HTMLInputElement;
const sysAdminSecret = document.getElementById('sysAdminSecret') as HTMLInputElement;
const saveSystemConfigBtn = document.getElementById('saveSystemConfigBtn') as HTMLButtonElement;

if (sysWorkerUrl) sysWorkerUrl.value = currentWorkerUrl;
if (sysAdminSecret) sysAdminSecret.value = currentAdminSecret;

if (saveSystemConfigBtn) {
  saveSystemConfigBtn.addEventListener('click', () => {
    currentWorkerUrl = sysWorkerUrl.value.trim() || DEFAULT_WORKER_URL;
    currentAdminSecret = sysAdminSecret.value.trim();
    localStorage.setItem('ppp_admin_worker_url', currentWorkerUrl);
    localStorage.setItem('ppp_admin_secret', currentAdminSecret);
    showToast('Master configuration saved!');
  });
}

// Master Auth Modal Handlers
const adminAuthBtn = document.getElementById('adminAuthBtn') as HTMLButtonElement;
const masterAuthModal = document.getElementById('masterAuthModal') as HTMLDivElement;
const closeMasterAuthBtn = document.getElementById('closeMasterAuthBtn') as HTMLButtonElement;
const saveMasterAuthBtn = document.getElementById('saveMasterAuthBtn') as HTMLButtonElement;
const modalAdminSecret = document.getElementById('modalAdminSecret') as HTMLInputElement;

if (adminAuthBtn) {
  adminAuthBtn.addEventListener('click', () => {
    if (modalAdminSecret) modalAdminSecret.value = currentAdminSecret;
    if (masterAuthModal) masterAuthModal.classList.remove('hidden');
  });
}

if (closeMasterAuthBtn) {
  closeMasterAuthBtn.addEventListener('click', () => {
    if (masterAuthModal) masterAuthModal.classList.add('hidden');
  });
}

if (saveMasterAuthBtn && modalAdminSecret) {
  saveMasterAuthBtn.addEventListener('click', () => {
    const val = modalAdminSecret.value.trim();
    if (!val) {
      showToast('Please enter master passkey', true);
      return;
    }
    currentAdminSecret = val;
    localStorage.setItem('ppp_admin_secret', val);
    if (sysAdminSecret) sysAdminSecret.value = val;
    if (masterAuthModal) masterAuthModal.classList.add('hidden');
    showToast('Authenticated with master secret!');
  });
}

// Initial Boot
renderNodesRegistry();
renderFleetCards();
